import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, Trash2 } from "lucide-react";
import {
  AppButton, Callout, ConfirmModal, EmptyState, IconButton, StatusBadge, VideoPlayer, cn,
} from "../../ui";
import Shimmer from "../../Common/Shimmer";
import apiClient from "../../../utils/apiClient";
import { formatBytes } from "../../../utils/format";

const formatWhen = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" });
  } catch {
    return "";
  }
};

const formatLength = (minutes) => {
  const m = Math.round(Number(minutes) || 0);
  if (!m) return "";
  if (m < 60) return `${m} min`;
  return m % 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${Math.floor(m / 60)} h`;
};

/** Who can watch what is on this tab, said once at the top of it. */
const audienceSentence = ({ sharedWithAttendees, hasPastedLink }, noun) => {
  if (hasPastedLink) return "Attendees get the link you pasted in Edit, not these files. Your staff can watch them here.";
  if (sharedWithAttendees) return `Attendees can watch this from their ${noun} link. Your staff can watch it here.`;
  return "Only your staff can see this. To share it, choose “Share with attendees” in Edit.";
};

/**
 * RecordingsPanel — what was recorded of a demo or webinar on Grats Meet.
 *
 * Nobody has to do anything for a recording to land here: the webhook brings it,
 * and a sweep every 15 minutes fetches whatever the webhook did not. Sync is for
 * someone who cannot wait. `canSync` and `canDelete` are decided by the page.
 */
const RecordingsPanel = ({ apiBase, sessionId, sessionStatus, noun = "session", canSync = false, canDelete = false, notify }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(null);

  const url = `${apiBase}/sessions/${sessionId}/recordings`;

  // Called from the player too, when a link expires mid-watch: it must not go
  // through `loading`, or the player would unmount under the viewer.
  const load = useCallback(async () => {
    try {
      const { data: res } = await apiClient.get(url);
      setData(res);
    } catch (err) {
      notify?.(err?.response?.data?.message || "Failed to load the recordings.", "error");
    }
    setLoading(false);
  }, [url, notify]);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data: res } = await apiClient.post(`${url}/sync`);
      notify?.(res?.message || "Checked for recordings.", "success");
      await load();
    } catch (err) {
      notify?.(
        err?.response?.data?.message
          || "This is taking a while. A long recording keeps copying in the background, so check again in a few minutes.",
        "error",
      );
    }
    setSyncing(false);
  };

  const remove = async () => {
    setBusy(true);
    try {
      await apiClient.delete(`${url}/${removing._id}`);
      notify?.("Recording deleted.", "success");
      setRemoving(null);
      await load();
    } catch (err) {
      notify?.(err?.response?.data?.message || "Failed to delete the recording.", "error");
    }
    setBusy(false);
  };

  if (loading) return <Shimmer height={220} />;

  const items = data?.items || [];
  const ready = items.filter((r) => r.status === "ready");
  const current = ready.find((r) => r._id === selectedId) || ready[0] || null;
  const failed = items.some((r) => r.status === "failed");
  const ended = sessionStatus === "ended";
  const recordingOff = data && !data.recordingOn;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
        <p className="tw-m-0 tw-text-sm tw-text-content-muted">{data ? audienceSentence(data, noun) : ""}</p>
        {canSync && ended && !recordingOff && (
          <AppButton size="sm" variant="outline" onClick={sync} disabled={syncing}>
            <RefreshCw size={16} /> {syncing ? "Checking…" : "Sync now"}
          </AppButton>
        )}
      </div>

      {recordingOff && (
        <Callout tone="info">
          Recording is off for this {noun}, so nothing is kept here. Tick “Record this {noun}” in Edit and it arrives
          within about 15 minutes.
        </Callout>
      )}
      {failed && (
        <Callout tone="warning">
          A recording could not be saved. It is tried again automatically{canSync ? ", or press Sync now" : ""}.
        </Callout>
      )}

      {current && (
        <VideoPlayer
          src={current.url}
          poster={current.thumbnailUrl}
          mediaKey={current._id}
          onExpired={load}
        />
      )}

      {!items.length && !recordingOff && (
        <EmptyState
          title="No recording yet"
          description={ended
            ? "It arrives by itself within about 15 minutes of being ready. A long session takes a while to prepare."
            : `The recording arrives here after the ${noun} ends.`}
        />
      )}

      {items.length > 0 && (
        <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
          {items.map((row) => {
            const playing = current?._id === row._id;
            const facts = [formatWhen(row.recordedAt), formatLength(row.duration), row.fileSize ? formatBytes(row.fileSize) : ""]
              .filter(Boolean).join(" · ");
            return (
              <li
                key={row._id}
                className={cn(
                  "tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded tw-border tw-p-3",
                  playing ? "tw-border-primary" : "tw-border-line",
                )}
              >
                <div className="tw-min-w-0">
                  <div className="tw-truncate tw-text-sm tw-font-medium tw-text-content">{row.title}</div>
                  <div className="tw-truncate tw-text-xs tw-text-content-subtle">{facts}</div>
                </div>
                <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
                  {row.status === "failed" && <StatusBadge status="danger">Not saved</StatusBadge>}
                  {row.status === "ready" && ready.length > 1 && !playing && (
                    <AppButton size="sm" variant="outline" onClick={() => setSelectedId(row._id)}>Play</AppButton>
                  )}
                  {canDelete && (
                    <IconButton
                      label={`Delete ${row.title}`}
                      icon={Trash2}
                      variant="ghost-destructive"
                      size="sm"
                      onClick={() => setRemoving(row)}
                    />
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmModal
        show={!!removing}
        onHide={() => setRemoving(null)}
        onConfirm={remove}
        title="Delete this recording?"
        message="The file is removed from the LMS for good and frees its storage. It will not be fetched again."
        confirmText="Delete"
        variant="danger"
        loading={busy}
      />
    </div>
  );
};

export default RecordingsPanel;
